import { useI18n } from '@/contexts/I18nContext';
import { formatPriceCents, type RollReviewPricing, type TierKey } from '../domain/rollReviewPricing';

interface TierPricingTableProps {
  pricing: RollReviewPricing;
  highlightedTierKey?: TierKey;
}

export function TierPricingTable({ pricing, highlightedTierKey }: TierPricingTableProps) {
  const { t } = useI18n();

  return (
    <div
      className="w-full rounded-2xl border border-white/[0.06] bg-[#231a1a] pt-[5px] pb-px px-[17px]"
      data-testid="tier-pricing-table"
    >
      {pricing.tiers.map((tier, index) => {
        const isHighlighted = tier.key === highlightedTierKey;
        const isLast = index === pricing.tiers.length - 1;

        return (
          <div
            key={tier.key}
            data-testid={`tier-row-${tier.key}`}
            data-highlighted={isHighlighted ? 'true' : 'false'}
            className={[
              'flex items-center justify-between w-full',
              isLast ? 'h-[40px] py-3' : 'h-[41px] pt-3 pb-[13px] border-b border-white/[0.06]',
              isHighlighted ? 'relative' : '',
            ].join(' ')}
          >
            {/* highlighted left accent bar */}
            {isHighlighted && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-3/5 w-[3px] rounded-full bg-sossiety-red" />
            )}

            {/* label */}
            <p
              className={[
                'text-[13px] leading-normal font-normal whitespace-nowrap pl-3',
                isHighlighted ? 'text-white font-medium' : 'text-sossiety-grey-300',
              ].join(' ')}
            >
              {tier.label}
              {isHighlighted && (
                <span className="ml-2 text-[11px] font-normal text-sossiety-grey-300">
                  {t('roll_review.tier_detected_label', { number: tier.key.replace('tier_', '') })}
                </span>
              )}
            </p>

            {/* price */}
            <p
              className={[
                'text-[13px] leading-normal font-medium whitespace-nowrap',
                isHighlighted ? 'text-sossiety-red' : 'text-white',
              ].join(' ')}
            >
              {formatPriceCents(tier.priceCents, pricing.currency)}
            </p>
          </div>
        );
      })}
    </div>
  );
}
